/**
 * The Atlas event log and the plot sampler's folded sections: the six newest
 * rows drawn into `#atlas-event-log`, and which sampler sections stay closed.
 */

import { escapeHtml } from '../../html.ts';
import type { SubPlot } from '../plotField';

// ── Event log ─────────────────────────────────────────────────────────
// Bottom-left, six lines, newest first, on the sol clock. Rows are clickable
// and fly the camera to the cell.
export interface LogEntry {
  text: string;
  /** Cell coords for camera fly-to. */
  cellX?: number;
  cellZ?: number;
  /** Timestamp label (sol-relative). */
  time: string;
}

export interface EventLogHandle {
  readonly eventLog: readonly LogEntry[];
  logEvent(text: string, cell?: SubPlot): void;
  toggleSection(id: string): void;
  sect(id: string, title: string, body: string): string;
}

export function createEventLog(): EventLogHandle {
  const eventLog: LogEntry[] = [];
  const MAX_LOG = 6;

  function logEvent(text: string, cell?: SubPlot): void {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    eventLog.unshift({
      text,
      cellX: cell?.x,
      cellZ: cell?.z,
      time,
    });
    if (eventLog.length > MAX_LOG) eventLog.length = MAX_LOG;
    renderEventLog();
  }

  function renderEventLog(): void {
    const el = document.getElementById('atlas-event-log');
    if (!el) return;
    el.innerHTML = eventLog.map((e, i) =>
      `<div class="atlas-log-row${e.cellX != null ? ' clickable' : ''}" data-log="${i}">` +
      `<span class="atlas-log-time">${escapeHtml(e.time)}</span> ${escapeHtml(e.text)}</div>`,
    ).join('');
  }

  // Which sampler sections are folded away. Held OUTSIDE the DOM because the
  // panel rewrites its own innerHTML on every survey — and on every re-survey
  // of a season sweep — so anything kept in the markup would be forgotten
  // several times a second.
  const SECT_KEY = 'atlas.sampler.collapsed';
  const stored = (() => {
    try { return JSON.parse(localStorage.getItem(SECT_KEY) ?? 'null') as string[] | null; }
    catch { return null; }
  })();
  const collapsed = new Set<string>(stored ?? ['evidence']);
  function toggleSection(id: string): void {
    if (collapsed.has(id)) collapsed.delete(id);
    else collapsed.add(id);
    try {
      localStorage.setItem(SECT_KEY, JSON.stringify([...collapsed]));
    } catch {
      /* private mode; the fold just won't persist */
    }
  }
  function sect(id: string, title: string, body: string): string {
    return `<div class="atlas-sect${collapsed.has(id) ? ' closed' : ''}">
      <button class="atlas-sect-head" data-sect="${id}">${title}<i></i></button>
      <div class="atlas-sect-body">${body}</div>
    </div>`;
  }

  return { eventLog, logEvent, toggleSection, sect };
}
